import { Tab, TabPanel, Tabs, TabsList } from '@/components/Tabs'
import type {
  CreateOperatingCostEntryInput,
  CreateOperatingCostMonthlyEntryInput,
} from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostCategory, OperatingCostKind } from '@/types/api'
import {
  CREDIT_PERCENT_TAB_LABEL,
  CREDIT_PERCENT_TAB_VALUE,
  customCategoriesByKind,
  defaultInnerTab,
  displayOperatingCostCategoryName,
  isAwsServerCategory,
  isCreditCardCategory,
  isDoeyCategory,
  isThirdPartySplitCommissionCategory,
  isWhatsAppApiCategory,
  kindMainTabLabel,
  OTHERS_TAB_VALUE,
  MARKETING_CATEGORY_SLUG,
  systemCategoriesByKind,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'
import { AwsServerCostPanel } from './AwsServerCostPanel'
import { CreditFeePanel } from './CreditFeePanel'
import { OperatingCostCategoryCard } from './OperatingCostCategoryCard'
import { PixFeePanel } from './PixFeePanel'
import { SplitCommissionCostPanel } from './SplitCommissionCostPanel'
import { WhatsAppApiCostPanel } from './WhatsAppApiCostPanel'

type OperatingCostKindPanelProps = {
  kind: OperatingCostKind
  categories: OperatingCostCategory[]
  isSaving: boolean
  onCreateCategory: () => void
  onRename: (categoryId: number, name: string) => Promise<boolean>
  onDeleteCategory: (categoryId: number) => Promise<boolean>
  onCreateEntry: (
    categoryId: number,
    input: CreateOperatingCostEntryInput
  ) => Promise<boolean>
  onUpdateEntry: (
    entryId: number,
    input: CreateOperatingCostEntryInput
  ) => Promise<boolean>
  onDeleteEntry: (entryId: number) => Promise<boolean>
  onCreateMonthlyEntry: (
    categoryId: number,
    input: CreateOperatingCostMonthlyEntryInput
  ) => Promise<boolean>
  onUpdateMonthlyEntry: (
    entryId: number,
    input: CreateOperatingCostMonthlyEntryInput
  ) => Promise<boolean>
  onDeleteMonthlyEntry: (entryId: number) => Promise<boolean>
  onUpdateUnitRate: (categoryId: number, amount: number) => Promise<boolean>
  onFeesChanged?: () => Promise<void> | void
}

export function OperatingCostKindPanel({
  kind,
  categories,
  isSaving,
  onCreateCategory,
  onRename,
  onDeleteCategory,
  onCreateEntry,
  onUpdateEntry,
  onDeleteEntry,
  onCreateMonthlyEntry,
  onUpdateMonthlyEntry,
  onDeleteMonthlyEntry,
  onUpdateUnitRate,
  onFeesChanged,
}: OperatingCostKindPanelProps) {
  const systemCategories = systemCategoriesByKind(categories, kind)
  const customCategories = customCategoriesByKind(categories, kind)
  const kindLabelText = kindMainTabLabel(kind)
  const othersLabel = kind === 'fixed' ? 'Outros custos fixos' : 'Outros custos variáveis'
  const hasCreditCategory = systemCategories.some(isCreditCardCategory)

  return (
    <div className={styles.kindShell}>
      <Tabs
        defaultTab={defaultInnerTab(systemCategories)}
        id={`operating-cost-categories-${kind}`}
      >
        <TabsList aria-label={`Categorias de custos ${kindLabelText.toLowerCase()}`}>
          {systemCategories.flatMap((category) => {
            const tab = (
              <Tab key={category.slug} value={category.slug}>
                {displayOperatingCostCategoryName(category)}
              </Tab>
            )
            if (kind === 'variable' && isDoeyCategory(category) && !hasCreditCategory) {
              return [
                tab,
                <Tab key={CREDIT_PERCENT_TAB_VALUE} value={CREDIT_PERCENT_TAB_VALUE}>
                  {CREDIT_PERCENT_TAB_LABEL}
                </Tab>,
              ]
            }
            return [tab]
          })}
          {kind === 'variable' &&
          !systemCategories.some(isDoeyCategory) &&
          !hasCreditCategory ? (
            <Tab value={CREDIT_PERCENT_TAB_VALUE}>{CREDIT_PERCENT_TAB_LABEL}</Tab>
          ) : null}
          <Tab value={OTHERS_TAB_VALUE}>Outros</Tab>
        </TabsList>

        {systemCategories.map((category) => (
          <TabPanel key={category.slug} value={category.slug}>
            <SystemCategoryPanel
              category={category}
              isSaving={isSaving}
              onRename={onRename}
              onDeleteCategory={onDeleteCategory}
              onCreateEntry={onCreateEntry}
              onUpdateEntry={onUpdateEntry}
              onDeleteEntry={onDeleteEntry}
              onCreateMonthlyEntry={onCreateMonthlyEntry}
              onUpdateMonthlyEntry={onUpdateMonthlyEntry}
              onDeleteMonthlyEntry={onDeleteMonthlyEntry}
              onUpdateUnitRate={onUpdateUnitRate}
              onFeesChanged={onFeesChanged}
            />
          </TabPanel>
        ))}

        {kind === 'variable' && !hasCreditCategory ? (
          <TabPanel value={CREDIT_PERCENT_TAB_VALUE}>
            <CreditFeePanel
              category={{
                id: 0,
                slug: 'credit_card',
                name: CREDIT_PERCENT_TAB_LABEL,
                kind: 'variable',
                value_type: 'percent',
                is_system: true,
                sort_order: 8,
                unit_amount: null,
                total_amount: 0,
                total_rate: null,
                fee_summary: null,
                entries: [],
                monthly_entries: [],
              }}
              onSaved={onFeesChanged}
            />
          </TabPanel>
        ) : null}

        <TabPanel value={OTHERS_TAB_VALUE}>
          <div className={styles.othersToolbar}>
            <p className={styles.othersCopy}>
              {othersLabel}. Crie categorias personalizadas neste grupo.
            </p>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={onCreateCategory}
              disabled={isSaving}
            >
              Nova categoria
            </button>
          </div>

          {customCategories.length === 0 ? (
            <p className={styles.empty}>Nenhuma categoria personalizada neste grupo.</p>
          ) : (
            <div className={styles.categoryGrid}>
              {customCategories.map((category) => (
                <OperatingCostCategoryCard
                  key={category.id}
                  category={category}
                  isSaving={isSaving}
                  onRename={onRename}
                  onDeleteCategory={onDeleteCategory}
                  onCreateEntry={onCreateEntry}
                  onUpdateEntry={onUpdateEntry}
                  onDeleteEntry={onDeleteEntry}
                />
              ))}
            </div>
          )}
        </TabPanel>
      </Tabs>
    </div>
  )
}

type SystemCategoryPanelProps = Omit<
  OperatingCostKindPanelProps,
  'kind' | 'categories' | 'onCreateCategory'
> & {
  category: OperatingCostCategory
}

function SystemCategoryPanel({
  category,
  isSaving,
  onRename,
  onDeleteCategory,
  onCreateEntry,
  onUpdateEntry,
  onDeleteEntry,
  onCreateMonthlyEntry,
  onUpdateMonthlyEntry,
  onDeleteMonthlyEntry,
  onUpdateUnitRate,
  onFeesChanged,
}: SystemCategoryPanelProps) {
  if (isWhatsAppApiCategory(category)) {
    return (
      <WhatsAppApiCostPanel
        category={category}
        isSaving={isSaving}
        onUpdateUnitRate={onUpdateUnitRate}
      />
    )
  }

  if (isDoeyCategory(category)) {
    return <PixFeePanel category={category} onSaved={onFeesChanged} />
  }

  if (isCreditCardCategory(category)) {
    return <CreditFeePanel category={category} onSaved={onFeesChanged} />
  }

  if (isThirdPartySplitCommissionCategory(category)) {
    return <SplitCommissionCostPanel category={category} />
  }

  if (isAwsServerCategory(category) || category.slug === MARKETING_CATEGORY_SLUG) {
    return (
      <AwsServerCostPanel
        category={category}
        isSaving={isSaving}
        onCreateMonthlyEntry={onCreateMonthlyEntry}
        onUpdateMonthlyEntry={onUpdateMonthlyEntry}
        onDeleteMonthlyEntry={onDeleteMonthlyEntry}
      />
    )
  }

  return (
    <OperatingCostCategoryCard
      category={category}
      isSaving={isSaving}
      onRename={onRename}
      onDeleteCategory={onDeleteCategory}
      onCreateEntry={onCreateEntry}
      onUpdateEntry={onUpdateEntry}
      onDeleteEntry={onDeleteEntry}
    />
  )
}
